import type { LayerState } from '@vitals/shared';
import type { VitalDef, VitalDetail } from './vitals.js';

interface AdaOutage {
  station: string;
  kind: 'elevator' | 'escalator' | 'equipment';
  serving?: string;
  reason?: string;
  estReturn?: string;
}

function isAdaOutage(value: unknown): value is AdaOutage {
  if (typeof value !== 'object' || value === null) return false;
  const outage = value as Record<string, unknown>;
  return typeof outage.station === 'string'
    && (outage.kind === 'elevator' || outage.kind === 'escalator' || outage.kind === 'equipment')
    && (outage.serving === undefined || typeof outage.serving === 'string')
    && (outage.reason === undefined || typeof outage.reason === 'string')
    && (outage.estReturn === undefined || typeof outage.estReturn === 'string');
}

function outageMeta(outage: AdaOutage): string | undefined {
  const parts = [
    outage.serving,
    outage.reason,
    outage.estReturn ? `Est. return ${outage.estReturn}` : undefined,
  ].filter((part): part is string => typeof part === 'string' && part.length > 0);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/**
 * Vital selector for elevator/escalator outages.
 *
 * Display: value = total currently out, label "elev/esc out".
 * No tone — NYC always has some equipment out, so there is no "all clear".
 * Down/no-summary: value "—".
 */
export const eneVital: VitalDef = {
  id: 'ene',
  layerId: 'ene',

  select(s: LayerState | undefined) {
    const isDown = !s || s.status === 'down' || s.summary == null;
    if (isDown) {
      return {
        value: '—',
        label: 'elev/esc out',
        down: true,
        stale: false,
      };
    }

    const stale = s.status === 'stale';
    const { out, elevators, escalators, ada, adaOutages } = s.summary as {
      out: number;
      elevators: number;
      escalators: number;
      ada: number;
      adaOutages?: unknown;
    };
    const details: VitalDetail[] = [
      { kind: 'metric', label: 'Total', value: `${out}` },
      { kind: 'metric', label: 'Elevators', value: `${elevators}` },
      { kind: 'metric', label: 'Escalators', value: `${escalators}` },
      { kind: 'metric', label: 'ADA-path outages', value: `${ada}` },
    ];
    if (Array.isArray(adaOutages)) {
      for (const outage of adaOutages.filter(isAdaOutage)) {
        const meta = outageMeta(outage);
        details.push({
          kind: 'alert',
          title: `${outage.station} · ${outage.kind === 'elevator' ? 'Elevator' : outage.kind === 'escalator' ? 'Escalator' : 'Equipment'}`,
          ...(meta ? { meta } : {}),
        });
      }
      if (adaOutages.length === 0) details.push({ kind: 'note', text: 'No ADA-path outages' });
    }

    return {
      value: `${out}`,
      label: 'elev/esc out',
      down: false,
      stale,
      detailTitle: 'Elevator & escalator outages',
      details,
    };
  },
};
